import {
  Button,
  Card,
  CircularProgress,
  createStyles,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Typography,
  withStyles,
  WithStyles
} from "@material-ui/core";
import { GetAppOutlined } from "@material-ui/icons";
import axios from "axios";
import { LightThemeTable } from "components/LightThemeTable";
import config from "config";
import Constants from "helpers/constants";
import { getHeaders } from "helpers/headers";
import { getItem } from "helpers/storage";
import { StateComponent } from "interfaces/project";
import moment from "moment";
import React, { ChangeEvent, Component } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { State } from "state";
import { createAnalyticsEvent, EventTypes, Status } from "state/analytics";
import { CHAIN_ROLE } from "state/auth";
import { AuthUser } from "state/auth_user";
import { fetchBasicInfoChains } from "state/chains";
import { deleteFileMappings, deleteMultipleFileMappings, fetchAllProjectFiles } from "state/files";
import { fetchProject, updateProject } from "state/project";
import CommonFileUpload from "./CommonFileUpload";

const YellowSwitch = withStyles({
  switchBase: {
    "&$checked": { color: "#E5B42F" },
    "&$checked + $track": { backgroundColor: "#E5B42F" },
  },
  checked: {},
  track: {},
})(Switch);

/* Styles */
const styles = () =>
  createStyles({
    textField: {
      "& .MuiInputBase-root": {
        borderRadius: 15,
        fontSize: 12,
        height: 32,
        "& fieldset": {
          borderWidth: "1.5px",
          borderColor: "#D1D5DB",
          borderRadius: 15,
        },
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: 15,
        "& fieldset": { borderColor: "#ccc", borderRadius: 15 },
        "&:hover fieldset": { borderColor: "#888" },
        "&.Mui-focused fieldset": { borderColor: "#1976d2" },
      },
      "& .MuiFormHelperText-root": { fontSize: 12 },
    },
    label: {
      fontSize: 12,
      fontWeight: 500,
      color: "#333",
      marginBottom: 4,
      display: "flex",
      alignItems: "center",
      gap: 2,
    },
    requiredStar: { color: "red", marginLeft: 2 },

    /* ── Responsive helpers ── */
    cardRoot: {
      position: "relative" as const,
      padding: 24,
      borderRadius: 5,
      backgroundColor: "#fff",
      width: "100%",
      boxSizing: "border-box",
      "@media (max-width: 600px)": {
        padding: 12,
      },
    },
    saveRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap" as const,
      gap: 8,
      marginBottom: 5,
      marginTop: 5,
      width: "100%",
      boxSizing: "border-box" as const,
    },
    tableWrapper: {
      overflowX: "auto" as const,
      width: "100%",
    },
    fileHeaderRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap" as const,
      gap: 8,
      marginBottom: 8,
    },
    dropdownMenu: {
      position: "absolute" as const,
      top: 42,
      left: 0,
      right: 0,
      border: "1px solid #ccc",
      borderRadius: 6,
      backgroundColor: "#fff",
      zIndex: 1300,
      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
      maxHeight: 200,
      overflowY: "auto" as const,
    },
    loadingOverlay: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      zIndex: 1500,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column" as const,
      gap: 10,
    },
    loadingText: {
      fontSize: 12,
      fontWeight: 600,
      color: "#333",
    },
  });

/* Redux */
const connecter = connect(
  (state: State) => ({
    chains: state.chains.content,
    project: state.project.content,
    files: state.files.content?.data,
    authUser: state.auth_user.content,
  }),
  { updateProject, fetchBasicInfoChains, fetchProject, fetchAllProjectFiles, deleteFileMappings, deleteMultipleFileMappings }
);

type UploadAttachmentsLocalProps = { user?: AuthUser };
interface Step1FormProps {
  submitRef: React.MutableRefObject<(() => Promise<boolean>) | null>;
}
interface ApiErrorResponse {
  errors?: string[];
  error?: string;
}
interface ProjectCommentType {
  id?: number | string;
  name?: string;
}
interface ProjectFormSource {
  id?: number | string;
  chain?: { id?: number | string };
  name?: string;
  project_type?: string;
  project_season?: string;
  project_year?: string;
  expires_at?: string;
  est_complete_date?: string;
  validation_deadline?: string;
  timezone?: string;
  surveyTitle?: string;
  survey_url?: string;
  commentsAllowed?: number | string;
  automatedAnnouncement?: boolean;
  automatedDataProcessing?: boolean;
  commentTypes?: ProjectCommentType[];
}
interface ProjectFormPayload {
  chain: string | number;
  name: string;
  season_id: string | number;
  year: string;
  projectType: string | number;
  validation_deadline: string;
  commentType: number[];
  commentsAllowed: number | string | "";
  timezone_id: string | number;
  est_complete_date: string;
  expires_at: string;
  automatedAnnouncement: boolean;
  surveyLink: string;
  surveyTitle: string;
  automatedDataProcessing: boolean;
}
type PropsFromRedux = ConnectedProps<typeof connecter>;
type Props = WithStyles<typeof styles> &
  PropsFromRedux &
  UploadAttachmentsLocalProps &
  RouteComponentProps<{ id: string }> &
  Step1FormProps & { projectId?: string; project?: any };

/* ─────────────────────────────────────────── */
class CreateProjectStepOne extends Component<Props, StateComponent> {
  seasonMap: Record<string, number> = { Spring: 1, Summer: 2, Fall: 3, Winter: 4, SpecialProject: 5 };
  timezoneMap: Record<string, number> = { AKST: 5, CST: 2, EST: 1, HAST: 6, MST: 3, PST: 4 };
  commentTypeMap: Record<string, number> = {
    "General Comment": 1, "Add Product": 2, "Modify Product": 3, "Remove Product": 4, "Swap Product": 5,
  };

  getActiveProjectId = (props: Props = this.props): string => {
    return String(props.match.params.id || props.projectId || "");
  };
  
  isInternalProjectAdmin = (): boolean => {
    const chainPermissionString = getItem(CHAIN_ROLE);
    const chainPermission = JSON.parse(chainPermissionString || "[]");
    const permission = chainPermission;
    const isProjectAdmin = permission.role_name === Constants.INTERNAL_PROJECT_ADMIN;
    return isProjectAdmin;
  };
  isProjectAdmin = (): boolean => {
    const chainPermissionString = getItem(CHAIN_ROLE);
    const chainPermission = JSON.parse(chainPermissionString || "[]");
    const permission = chainPermission;
    return (permission.role_name === Constants.EXTERNAL_PROJECT_ADMIN || permission.role_name === Constants.INTERNAL_PROJECT_ADMIN);
  };

  isSystemAdmin = (): boolean => {
    const chainPermissionString = getItem(CHAIN_ROLE);
    const chainPermission = JSON.parse(chainPermissionString || "[]");
    const permission = chainPermission;
    return permission.role_name === Constants.AB_SYSTEM_ADMIN;
  };
  state: StateComponent = {
    chain: "",
    name: "",
    season: "",
    year: "",
    projectType: "",
    validationDate: "",
    commentType: [],
    commentsAllowed: "",
    estimatedResetDate: "",
    projectExpirationDate: "",
    timezone_id: 0,
    automatedAnnouncement: false,
    // automatedDataProcessing: true,
    automatedDataProcessing: !this.isProjectAdmin(), 
    surveyLink: "",
    surveyTitle: "",
    errors: {},
    files: [],
    chainSearch: "",
    openChainDropdown: false,
    openSeasonDropdown: false,
    openProjectTypeDropdown: false,
    openTimezoneDropdown: false,
    openCommentTypeDropdown: false,
    projectId: "",
    project: undefined,
    expires_at: "",
    est_complete_date: "",
    season_id: "",
    saving: false,
    isProjectLoading: false,
    saveMessage: "",
    showDeleteModal: false,
    fileToDelete: 0,
    mapTypeToDelete: 0,
    selectedFiles: [],
    isDeletingBulk: false,
    originalName: "",
    originalChain: "",
    tableCurrentPage: 1,
    cachedProjectFiles: [],
  };

  chainDropdownRef: React.RefObject<HTMLDivElement> = React.createRef();
  seasonDropdownRef: React.RefObject<HTMLDivElement> = React.createRef();
  projectTypeDropdownRef: React.RefObject<HTMLDivElement> = React.createRef();
  timezoneDropdownRef: React.RefObject<HTMLDivElement> = React.createRef();
  commentTypeDropdownRef: React.RefObject<HTMLDivElement> = React.createRef();

  resetFormForProjectSwitch = () => {
    this.setState({
      projectId: "",
      chain: "",
      name: "",
      originalName: "",
      originalChain: "",
      season: "",
      year: "",
      projectType: "",
      validationDate: "",
      commentType: [],
      commentsAllowed: "",
      estimatedResetDate: "",
      projectExpirationDate: "",
      timezone_id: 0,
      surveyLink: "",
      surveyTitle: "",
      chainSearch: "",
      selectedFiles: [],
      files: [],
      errors: {},
      tableCurrentPage: 1,
      cachedProjectFiles: [],
    });
  };

  isFileSharing = (): boolean => this.state.projectType === 2;

  getProjectPayloadFromState = (): ProjectFormPayload => {
    const {
      chain, name, season, year, projectType, validationDate, commentType,
      commentsAllowed, timezone_id, estimatedResetDate, projectExpirationDate,
      automatedAnnouncement, surveyLink, surveyTitle, automatedDataProcessing,
    } = this.state;

    return {
      chain, name, season_id: season, year, projectType,
      validation_deadline: validationDate, commentType, commentsAllowed, timezone_id,
      est_complete_date: estimatedResetDate, expires_at: projectExpirationDate,
      automatedAnnouncement, surveyLink, surveyTitle, automatedDataProcessing,
    };
  };

  getProjectPayloadFromProject = (project: ProjectFormSource): ProjectFormPayload => {
    const commentType = project.commentTypes
      ? project.commentTypes.map((ct) => ct.name ? this.commentTypeMap[ct.name] || Number(ct.id) : Number(ct.id))
      : [];

    return {
      chain: project.chain?.id || "",
      name: project.name || "",
      season_id: project.project_season ? this.seasonMap[project.project_season] || "" : "",
      year: project.project_year || "",
      projectType: project.project_type === "Validation" ? 1 : project.project_type === "File Sharing" ? 2 : "",
      validation_deadline: project.validation_deadline ? project.validation_deadline.split("T")[0] : "",
      commentType,
      commentsAllowed: project.commentsAllowed || "",
      timezone_id: project.timezone ? this.timezoneMap[project.timezone] || "" : "",
      est_complete_date: project.est_complete_date ? project.est_complete_date.split("T")[0] : "",
      expires_at: project.expires_at ? project.expires_at.split("T")[0] : "",
      automatedAnnouncement: project.automatedAnnouncement || false,
      surveyLink: project.survey_url || "",
      surveyTitle: project.surveyTitle || "",
      automatedDataProcessing: (project.name && project.name !== "Untitled" ? project.automatedDataProcessing ?? true : true),
    };
  };

  normalizeProjectPayload = (payload: ProjectFormPayload) => ({
    ...payload,
    chain: String(payload.chain || ""),
    season_id: String(payload.season_id || ""),
    projectType: String(payload.projectType || ""),
    commentType: [...payload.commentType].map(String).sort(),
    commentsAllowed: String(payload.commentsAllowed || ""),
    timezone_id: String(payload.timezone_id || ""),
  });

  isProjectFormDirty = (payload: ProjectFormPayload): boolean => {
    const project = this.props.project as ProjectFormSource | undefined;
    if (!project) return true;
    return JSON.stringify(this.normalizeProjectPayload(payload)) !== JSON.stringify(this.normalizeProjectPayload(this.getProjectPayloadFromProject(project)));
  };

  prefillFormFromProject(project: ProjectFormSource) {
    if (!project) return;
    const selectedCommentTypes = project.commentTypes
      ? project.commentTypes
        .map((ct) => ct.name ? this.commentTypeMap[ct.name] || Number(ct.id) : Number(ct.id))
        .filter((id) => Number.isFinite(id))
      : [];
    this.setState({
      projectId: String(project.id || ""),
      chain: String(project.chain?.id || ""),
      name: project.name || "",
      originalName: project.name || "",
      originalChain: project.chain?.id || "",
      projectType: project.project_type === "Validation" ? 1 : project.project_type === "File Sharing" ? 2 : "",
      season: project.project_season ? this.seasonMap[project.project_season] || "" : "",
      year: project.project_year || "",
      projectExpirationDate: project.expires_at ? project.expires_at.split("T")[0] : "",
      estimatedResetDate: project.est_complete_date ? project.est_complete_date.split("T")[0] : "",
      validationDate: project.validation_deadline ? project.validation_deadline.split("T")[0] : "",
        timezone_id: project.timezone ? this.timezoneMap[project.timezone] || "" : "",
      surveyTitle: project.surveyTitle || "",
      surveyLink: project.survey_url || "",
      commentsAllowed: project.commentsAllowed || "",
      automatedAnnouncement: project.automatedAnnouncement || false,
      automatedDataProcessing: (project.name && project.name !== "Untitled" ? project.automatedDataProcessing ?? true : true),
      commentType: selectedCommentTypes,
    });
  }

  public handleAfterUpload = (type: "Project File") => {
    const { project } = this.props;
    if (project) {
      const hasError = type === "Project File" ? (this.props as any)["Project File Error"] : false;
      createAnalyticsEvent({
        event: EventTypes.CREATE_NEW_PROJECT_UPLOAD,
        eventType: "Action",
        ...(project.name && project.chain?.name && { projectName: `${project.chain.name} - ${project.name}` }),
        projectType: project.project_type,
        fileUploadType: type,
        fileUploadStatus: hasError ? Status.FAILURE : Status.SUCCESS,
      });
    }
  };

  handleClickOutside = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (target.closest('[role="menuitem"]') || target.closest('.MuiMenuItem-root')) return;
    const dropdownRefs = [
      this.chainDropdownRef, this.seasonDropdownRef, this.projectTypeDropdownRef,
      this.timezoneDropdownRef, this.commentTypeDropdownRef,
    ];
    const isOutside = dropdownRefs.every(ref => !ref.current || !ref.current.contains(target));
    if (isOutside) {this.closeAllDropdowns();
      if (this.state.chain === "") {
        this.setState({ chainSearch: "" });
      }
    }
  };

  componentDidMount(): void {
    const activeProjectId = this.getActiveProjectId();
    const hasActiveProjectId = !!activeProjectId;
    const isProjectSyncedWithRoute =
      !!this.props.project && String(this.props.project.id) === String(activeProjectId);

    this.props.fetchBasicInfoChains();
    this.props.submitRef.current = this.handleSubmitAndReturn;
    if (this.props.project && (!hasActiveProjectId || isProjectSyncedWithRoute)) {
      this.prefillFormFromProject(this.props.project);
    }
    if (activeProjectId) {
      this.props.fetchAllProjectFiles(activeProjectId);
      if (!isProjectSyncedWithRoute) {
        this.setState({ isProjectLoading: true, saveMessage: "Loading project information..." });
        this.props.fetchProject(activeProjectId);
      }
    }
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount(): void {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  componentDidUpdate(prevProps: Props) {
    // if (this.props.project && this.props.project !== prevProps.project) {
    //   const { project } = this.props;
    //   if (project.id && prevProps.project?.id !== project.id) this.props.fetchAllProjectFiles(project.id);
    //   const hasExistingValues = this.state.chain || this.state.name || this.state.season || this.state.projectType;
    //   if (hasExistingValues) {
    //   if (!prevProps.project && project) { this.prefillFormFromProject(project); }
    //     return;
    //   }
    //   this.prefillFormFromProject(project);
    const activeProjectId = this.getActiveProjectId();
    const previousActiveProjectId = this.getActiveProjectId(prevProps);

    if (activeProjectId !== previousActiveProjectId) {
      this.resetFormForProjectSwitch();
      this.setState({
        isProjectLoading: true,
        saveMessage: "Loading project information...",
        selectedFiles: [],
        errors: {},
      });

      if (activeProjectId) {
        this.props.fetchProject(activeProjectId);
        this.props.fetchAllProjectFiles(activeProjectId);
      }
    }

    const currentProject = this.props.project;
    const previousProject = prevProps.project;
    if (!currentProject) return;

    const isCurrentProjectForRoute = !activeProjectId || String(currentProject.id) === String(activeProjectId);
    if (!isCurrentProjectForRoute) {
      if (!this.state.isProjectLoading) {
        this.setState({ isProjectLoading: true, saveMessage: "Loading project information..." });
      }
      return;
    }

    const isProjectSwitched = !!currentProject.id && currentProject.id !== previousProject?.id;
    const isStateOutOfSync = !!currentProject.id && String(currentProject.id) !== String(this.state.projectId);
    const isInitialProjectLoad = !previousProject && !!currentProject;

    if (isProjectSwitched && currentProject.id) {
      this.props.fetchAllProjectFiles(currentProject.id);
      this.setState({
        selectedFiles: [],
        saveMessage: "",
        errors: {},
      });
    }

    if (isProjectSwitched || isInitialProjectLoad || isStateOutOfSync) {
      this.prefillFormFromProject(currentProject);
    }

    if (this.state.isProjectLoading) {
      this.setState({ isProjectLoading: false, saveMessage: "" });
    }

    // Keep cachedProjectFiles in sync with Redux files, but only when files are actually available (not undefined during loading) to avoid table unmount.
    if (this.props.files && prevProps.files !== this.props.files) {
      this.setState({
        cachedProjectFiles: this.props.files.filter((f: any) => f.maptype === "Project"),
      });
    }
  }

  handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value, type } = e.target;
    this.setState({ [name]: type === "number" ? Number(value) : value } as unknown as Pick<StateComponent, keyof StateComponent>);
  };

  handleSwitchChange = (_: any, checked: boolean): void => { this.setState({ automatedAnnouncement: checked }); };
  handleDataProcessingSwitchChange = (_: any, checked: boolean): void => {
    this.setState({ automatedDataProcessing: checked });
    const { project } = this.props;
    const localKey = project.id ? `step4_users_${project.id}` : "step4_users";
    localStorage.removeItem(localKey);
  };

  handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    
  const isSaved = await this.handleSubmitAndReturn();
  if (!isSaved) return;

    const files = e.target.files ? Array.from(e.target.files) : [];
    this.setState({ files });
  };

  public handleDownloadClick = (  row : any) => {
    return window.open(`${config.API_SERVICE_URL}/api/file/${row.id}`);
  }
  handleDrop = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); this.setState({ files: Array.from(e.dataTransfer.files) }); };
  handleDragOver = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault();

  handleDeleteFileConfirmed = async () => {
    const { fileToDelete, mapTypeToDelete } = this.state;
    try {
      await this.props.deleteFileMappings(fileToDelete, mapTypeToDelete);
      await this.props.fetchAllProjectFiles(this.props.project?.id);
    } catch (err) { console.error("File delete failed", err); }
  };

  handleFileCheckboxChange = (mapid: number, maptype: number) => {
    const { selectedFiles } = this.state;
    const index = selectedFiles.findIndex((file: any) => file.mapid === mapid && file.maptype === maptype);
    if (index > -1) {
      const newSelectedFiles = [...selectedFiles];
      newSelectedFiles.splice(index, 1);
      this.setState({ selectedFiles: newSelectedFiles });
    } else {
      this.setState({ selectedFiles: [...selectedFiles, { mapid, maptype }] });
    }
  };

  handleBulkAction = async () => {
    const { selectedFiles } = this.state;
    if (!selectedFiles.length) return;
    this.setState({ isDeletingBulk: true });
    try {
      const selectedFilesMapIds = selectedFiles.map((item: any) => ({ mapid: Number(item.mapid), maptype: item.maptype }));
      await this.props.deleteMultipleFileMappings(selectedFilesMapIds);
      this.setState({ selectedFiles: [], isDeletingBulk: false });
      this.props.fetchAllProjectFiles(this.props.projectId);
    } catch (err) {
      console.error("Bulk delete failed:", err);
      this.setState({ isDeletingBulk: false });
    }
  };

  handleSubmitAndReturn = async (): Promise<boolean> => {
    if (this.state.isProjectLoading) {
      this.setState({ saveMessage: "Please wait until project data finishes loading." });
      return false;
    }

    const activeProjectId = this.getActiveProjectId();
    if (!activeProjectId || String(this.state.projectId) !== String(activeProjectId)) {
      this.setState({ saveMessage: "Please wait until the selected project finishes loading." });
      return false;
    }

    const isValid = await this.validate();
    if (!isValid) return false;
    this.setState({ saving: true, saveMessage: "" });
    const id = activeProjectId;
    if (!id) {
      this.setState({ saving: false, saveMessage: "Project id is missing. Please reload and try again." });
      return false;
    }
    const payload = this.getProjectPayloadFromState();
    try {
      if (this.isProjectFormDirty(payload)) {
        await this.props.updateProject(id, payload);
        await this.props.fetchProject(id);
        this.setState({ saving: false, saveMessage: "Project information saved successfully." });
      } else {
        this.setState({ saving: false, saveMessage: "No project information changes to save." });
      }
      setTimeout(() => this.setState({ saveMessage: "" }), 3000);
      return true;
    } catch (error: unknown) {
      const apiMessage = axios.isAxiosError<ApiErrorResponse>(error) ? error.response?.data?.errors?.[0] || error.response?.data?.error || "Failed to save. Try again." : "Failed to save. Try again.";
      this.setState({ saving: false, saveMessage: apiMessage });
      return false;
    }
  };

  validate = async (): Promise<boolean> => {
    const errors: Record<string, string> = {};
    const activeProjectId = this.getActiveProjectId();

    if (!activeProjectId || String(this.state.projectId) !== String(activeProjectId)) {
      this.setState({ saveMessage: "Please wait until the selected project finishes loading." });
      return false;
    }

    const {
      chain,
      name,
      timezone_id,
      projectExpirationDate,
      originalName,
      originalChain
    } = this.state;
    if (!chain) errors.chain = "This field is required";
    if (!name) errors.name = "This field is required";
    if (!timezone_id) errors.timezone_id = "This field is required";
    if (!projectExpirationDate) errors.projectExpirationDate = "This field is required";

    // ONLY call duplicate API if changed
    const isNameChanged = name?.trim() !== originalName;
    const isChainChanged = String(chain) !== String(originalChain);

    if ((isNameChanged || isChainChanged) && chain && name?.trim()) {
      const isDuplicate = await this.checkDuplicateProjectName(
        chain,
        name.trim(),
        activeProjectId || undefined
      );
      if (isDuplicate) {
        errors.name = "A project with this name already exists for the selected chain";
      }
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  checkDuplicateProjectName = async (chainId: number | string, projectName: string, excludeProjectId?: string): Promise<boolean> => {
    try {
      const response = await axios.get(
        `${config.API_SERVICE_URL}/api/projects`,
        getHeaders()
      );
      const projects = response.data || [];
      return projects.some((project: any) => {
        if (!project.project_name || project.project_name.toLowerCase().trim() !== projectName.toLowerCase().trim()) {
          return false;
        }
        if (String(project.chain_id) !== String(chainId)) {
          return false;
        }
        if (excludeProjectId && String(project.project_id) === String(excludeProjectId)) {
          return false;
        }
        return true;
      });
    } catch (error) {
      console.error("Failed to validate project name uniqueness", error);
      return false;
    }
  };

  renderLabeledInput = (
    label: string,
    name: keyof StateComponent,
    required = false,
    props: Record<string, any> = {}
  ) => {
    const { classes } = this.props;
    const { errors } = this.state;
    const isDisabled = name === "name" && !!this.props.project?.name && this.props.project.name !== "Untitled";
    const fieldDisabled = props.disabled || isDisabled;
    let displayValue = this.state[name] ?? "";
    if (name === "name" && displayValue === "Untitled") displayValue = "";
    return (
      <div>
        <Typography className={classes.label} style={{ color: fieldDisabled ? "#999" : "#333" }}>
          {label}
          {required && <span className={classes.requiredStar}>*</span>}
        </Typography>
        <TextField
          name={name}
          variant="outlined"
          fullWidth
          size="small"
          placeholder={label}
          error={!!errors[name]}
          helperText={errors[name]}
          value={displayValue}
          onChange={this.handleChange}
          className={classes.textField}
          disabled={isDisabled}
          {...props}
          inputProps={{ autoComplete: "off", ...(props.inputProps || {}) }}
        />
      </div>
    );
  };

  closeAllDropdowns = () => {
    this.setState({
      openChainDropdown: false, openSeasonDropdown: false, openProjectTypeDropdown: false,
      openTimezoneDropdown: false, openCommentTypeDropdown: false,
    });
  };

  renderDropdown = (
    label: string,
    name: keyof StateComponent,
    openKey: keyof StateComponent,
    list: any[],
    valueKey = "id",
    labelKey = "name",
    required = false
  ) => {
    const { classes } = this.props;
    const { errors } = this.state;
    const open = this.state[openKey] as boolean;
    const value = this.state[name] as number | string | null;
    const selectedItem = list.find((item) => String(item[valueKey]) === String(value));
    const displayValue = selectedItem ? selectedItem[labelKey] : "";

    return (
      /* ── RESPONSIVE: xs=12 sm=6 md=3 ── */
      <Grid item xs={12} sm={6} md={3}>
        <Typography className={classes.label}>
          {label}
          {required && <span className={classes.requiredStar}>*</span>}
        </Typography>
        <div style={{ position: "relative" }}>
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            name={name}
            placeholder={`Select ${label}`}
            value={displayValue}
            onClick={() => { this.closeAllDropdowns(); this.setState({ [openKey]: !open } as any); }}
            error={!!errors[name]}
            helperText={errors[name]}
            className={classes.textField}
            autoComplete="off"
            InputProps={{
              style: { fontSize: 12, height: 32, cursor: "pointer", paddingRight: 30, borderRadius: 10 },
              readOnly: true,
              endAdornment: (
                <span style={{
                  position: "absolute", right: 12, top: "50%",
                  transform: `translateY(-50%) rotate(${open ? "270deg" : "90deg"})`,
                  color: "#888", fontSize: 18, pointerEvents: "none",
                  transition: "transform 0.2s ease", fontWeight: 600,
                }}>›</span>
              ),
            }}
          />
          {open && (
            <div className={classes.dropdownMenu}>
              {list.map((item) => (
                <MenuItem
                  key={item[valueKey]}
                  value={item[valueKey]}
                  onClick={() => this.setState({ [name]: item[valueKey], [openKey]: false } as any)}
                  style={{ fontSize: 12 }}
                >
                  {item[labelKey]}
                </MenuItem>
              ))}
            </div>
          )}
        </div>
      </Grid>
    );
  };

  renderMultiSelectDropdown = (
    label: string,
    name: keyof StateComponent,
    openKey: keyof StateComponent,
    list: any[],
    valueKey = "id",
    labelKey = "name",
    required = false,
    disabled = false
  ) => {
    const { classes } = this.props;
    const { errors } = this.state;
    const open = this.state[openKey] as boolean;
    const selectedValues = (this.state[name] as number[]) || [];
    const selectedItems = list.filter((item) => selectedValues.includes(item[valueKey]));
    const displayValue = selectedItems.length > 0 ? `${selectedItems.length} selected` : "";

    const handleToggle = (itemValue: number) => {
      const currentValues = [...selectedValues];
      const index = currentValues.indexOf(itemValue);
      if (index > -1) currentValues.splice(index, 1);
      else currentValues.push(itemValue);
      this.setState({ [name]: currentValues } as any);
    };

    return (
      /* ── RESPONSIVE: xs=12 sm=6 md=3 ── */
      <Grid item xs={12} sm={6} md={3}>
        <Typography className={classes.label} style={{ color: disabled ? "#999" : "#333" }}>
          {label}
          {required && <span className={classes.requiredStar}>*</span>}
        </Typography>
        <div style={{ position: "relative" }}>
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            name={name}
            placeholder={`Select ${label}`}
            value={displayValue}
            onClick={() => { if (!disabled) { this.closeAllDropdowns(); this.setState({ [openKey]: !open } as any); } }}
            error={!!errors[name]}
            helperText={errors[name]}
            className={classes.textField}
            autoComplete="off"
            disabled={disabled}
            InputProps={{
              style: { fontSize: 12, height: 32, cursor: "pointer", paddingRight: 30, borderRadius: 10 },
              readOnly: true,
              endAdornment: (
                <span style={{
                  position: "absolute", right: 12, top: "50%",
                  transform: `translateY(-50%) rotate(${open ? "270deg" : "90deg"})`,
                  color: "#888", fontSize: 18, pointerEvents: "none",
                  transition: "transform 0.2s ease", fontWeight: 600,
                }}>›</span>
              ),
            }}
          />
          {open && (
            <div className={classes.dropdownMenu}>
              {list.map((item) => {
                const isSelected = selectedValues.includes(item[valueKey]);
                return (
                  <MenuItem
                    key={item[valueKey]}
                    value={item[valueKey]}
                    onClick={() => handleToggle(item[valueKey])}
                    style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 8, padding: "8px 16px" }}
                  >
                    <div style={{
                      position: "relative", width: 16, height: 16,
                      border: `2px solid ${isSelected ? "#E5B611" : "#ccc"}`,
                      borderRadius: 3, backgroundColor: isSelected ? "#E5B611" : "#fff",
                      cursor: "pointer", display: "flex", alignItems: "center",
                      justifyContent: "center", transition: "all 0.2s ease",
                    }}>
                      {isSelected && (
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <span>{item[labelKey]}</span>
                  </MenuItem>
                );
              })}
            </div>
          )}
        </div>
      </Grid>
    );
  };

  handleSelectAllProjectFiles = () => {
    const projectFiles = this.props.files?.filter((f: any) => f.maptype === "Project") || [];
    if (this.state.selectedFiles.length === projectFiles.length) {
      this.setState({ selectedFiles: [] });
    } else {
      this.setState({ selectedFiles: projectFiles.map((file: any) => ({ mapid: file.mapid, maptype: file.maptype })) });
    }
  };

  render() {
    const { classes } = this.props;
    const { project } = this.props;
    const projectFiles = this.state.cachedProjectFiles;
    const enableUpload = project && project.name && project.name !== "Untitled" && project.chain?.name;
    const acceptedProjectFileTypes = ".pdf, .xlsx, .xlsm, .doc, .docx, .dotx, .docm, .ppt, .pps, .pptx, .pptm, .sldx, .sldm";
    const today = new Date().toISOString().split("T")[0];
    const allProjectFilesSelected =
      projectFiles &&
      projectFiles.length > 0 &&
      this.state.selectedFiles.length === projectFiles.length;

    const CheckboxCell = ({ isSelected, onClick }: { isSelected: boolean; onClick: () => void }) => (
      <div
        style={{
          width: 16, height: 16,
          border: `2px solid ${isSelected ? "#E5B611" : "#ccc"}`,
          borderRadius: 3, backgroundColor: isSelected ? "#E5B611" : "#fff",
          cursor: "pointer", display: "flex", alignItems: "center",
          justifyContent: "center", transition: "all 0.2s ease",
        }}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
      >
        {isSelected && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M1 4L3.5 6.5L9 1" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
    );

    const fileColumns = [
      {
        field: "checkbox",
        header: (
          <div
            onClick={(e) => {
              e.stopPropagation();
              this.handleSelectAllProjectFiles();
            }}
            style={{
              width: 16, height: 16,
              border: `2px solid ${allProjectFilesSelected ? "#E5B611" : "#ccc"}`,
              borderRadius: 3,
              backgroundColor: allProjectFilesSelected ? "#E5B611" : "#fff",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            {allProjectFilesSelected && (
              <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                <path d="M1 4L3.5 6.5L9 1" stroke="#000" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        ),
        width: "40px",
        formatter: (_: any, row: any) => {
          const isSelected = this.state.selectedFiles.some(
            (file: any) => file.mapid === row.mapid && file.maptype === row.maptype
          );
          return (
            <div
              onClick={(e) => {
                e.stopPropagation();
                this.handleFileCheckboxChange(row.mapid, row.maptype);
              }}
              style={{
                width: 16, height: 16,
                border: `2px solid ${isSelected ? "#E5B611" : "#ccc"}`,
                borderRadius: 3,
                backgroundColor: isSelected ? "#E5B611" : "#fff",
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              {isSelected && (
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="#000" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
          );
        },
      },
      {
        field: "filename",
        header: "File Name",
        formatter: (_: any, row: any) => <div>{row.filename}</div>,
      },
      {
        field: "created_at",
        header: "Uploaded Date",
        formatter: (_: any, row: any) => <div>{moment(row.created_at).format("MM-DD-YYYY")}</div>,
      },
      {
        field: "actions",
        header: "Action",
        width: "60px",
        formatter: (_: any, row: any) => (
          <>
            <img
              src={require("../../assets/img/Delete.png")}
              alt="delete"
              style={{ cursor: "pointer", width: 22, height: 22, padding: 4 }}
              onClick={() => this.setState({ showDeleteModal: true, fileToDelete: row.mapid, mapTypeToDelete: row.maptype })}
            />
          <IconButton size="small" style={{ padding: 0, marginLeft: 8 }} onClick={() => this.handleDownloadClick(row)}>
            <GetAppOutlined style={{ color: "#E5B611" }} />
          </IconButton>
          </>
        ),
      },
    ];

    return (
      <Card className={classes.cardRoot}>
        {this.state.isProjectLoading && (
          <div className={classes.loadingOverlay}>
            <CircularProgress size={26} style={{ color: "#E5B42F" }} />
            <Typography className={classes.loadingText}>Loading project details...</Typography>
          </div>
        )}
        <Grid container spacing={2}>

          {/* ── Chain (custom searchable dropdown) ── */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography className={classes.label}>
              Chain<span className={classes.requiredStar}>*</span>
            </Typography>
            {this.props.project?.chain?.id ? (
              <TextField
                variant="outlined" fullWidth size="small"
                value={this.props.project.chain.name}
                disabled className={classes.textField} autoComplete="off"
              />
            ) : (
              <div style={{ position: "relative" }} ref={this.chainDropdownRef}>
                <TextField
                  variant="outlined" fullWidth size="small" name="chain"
                  placeholder="Select Chain"
                  value={
                    this.state.chain
                      ? this.props.chains?.find((c: any) => c.id === this.state.chain)?.name || ""
                      : this.state.chainSearch || ""
                  }
                  onClick={() => { this.closeAllDropdowns(); this.setState({ openChainDropdown: !this.state.openChainDropdown }); }}
                  onChange={(e) => this.setState({ chainSearch: e.target.value, chain: "", openChainDropdown: true })}
                  autoComplete="off"
                  error={!!this.state.errors.chain}
                  helperText={this.state.errors.chain}
                  className={classes.textField}
                  InputProps={{
                    style: { fontSize: 12, height: 32, cursor: "pointer", paddingRight: 30 },
                    endAdornment: (
                      <span style={{
                        position: "absolute", right: 12, top: "50%",
                        transform: `translateY(-50%) rotate(${this.state.openChainDropdown ? "270deg" : "90deg"})`,
                        color: "#888", fontSize: 18, pointerEvents: "none",
                        transition: "transform 0.2s ease", fontWeight: 600,
                      }}>›</span>
                    ),
                  }}
                />
                {this.state.openChainDropdown && (
                  <div className={classes.dropdownMenu}>
                    {this.props.chains
                      ?.filter((chain: any) => chain.name.toLowerCase().includes((this.state.chainSearch || "").toLowerCase()))
                      .map((chain: any) => (
                        <MenuItem
                          key={chain.id} value={chain.id}
                          onClick={() => this.setState({ chain: chain.id, openChainDropdown: false, chainSearch: chain.name } as any)}
                          style={{ fontSize: 12 }}
                        >
                          {chain.name}
                        </MenuItem>
                      ))}
                    {this.props.chains?.filter((chain: any) =>
                      chain.name.toLowerCase().includes((this.state.chainSearch || "").toLowerCase())
                    ).length === 0 && (
                        <MenuItem disabled style={{ fontSize: 12 }}>No results found</MenuItem>
                      )}
                  </div>
                )}
              </div>
            )}
          </Grid>

          {/* ── Project Name ── */}
          <Grid item xs={12} sm={6} md={3}>
            {this.renderLabeledInput("Project Name", "name", true)}
          </Grid>

          {/* ── Season ── */}
          {this.renderDropdown("Season", "season", "openSeasonDropdown",
            [{ id: 1, name: "Spring" }, { id: 2, name: "Summer" }, { id: 3, name: "Fall" }, { id: 4, name: "Winter" }, { id: 5, name: "Special Project" }],
            "id", "name", false
          )}

          {/* ── Year ── */}
          {/* <Grid item xs={12} sm={6} md={3}>
            {this.renderLabeledInput("Year", "year")}
          </Grid> */}
          <Grid item xs={12} sm={6} md={3}>
            {this.renderLabeledInput("Year", "year", false, {
              inputProps: { autoComplete: "off", inputMode: "numeric", pattern: "[0-9]*" },
              onKeyDown: (e: React.KeyboardEvent) => {
                if (!/[0-9]/.test(e.key) && !["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) e.preventDefault();
              },
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                const val = e.target.value.replace(/\D/g, "");
                this.setState({ year: val } as any);
              },
            })}
          </Grid>

          {/* ── Project Type ── */}
          {this.renderDropdown("Project Type", "projectType", "openProjectTypeDropdown",
            [{ id: 1, name: "Validation" }, { id: 2, name: "File Sharing" }],
            "id", "name", false
          )}

          {/* ── Validation Date ── */}
          <Grid item xs={12} sm={6} md={3}>
            {this.renderLabeledInput("Validation Date", "validationDate", false, {
              type: "date", InputLabelProps: { shrink: true }, inputProps: { min: today },
              disabled: this.isFileSharing(),
            })}
          </Grid>

          {/* ── Comment Type ── */}
          {this.renderMultiSelectDropdown(
            "Type of Comments Allowed", "commentType", "openCommentTypeDropdown",
            [
              { id: 1, name: "General Comment" }, { id: 2, name: "Add Product" },
              { id: 3, name: "Modify Product" }, { id: 4, name: "Remove Product" }, { id: 5, name: "Swap Product" },
            ],
            "id", "name", false, this.isFileSharing()
          )}

          {/* ── Comments Allowed ── */}
          {/* <Grid item xs={12} sm={6} md={3}>
            {this.renderLabeledInput("Number of Comments Allowed", "commentsAllowed", false, {
              type: "text", placeholder: "Enter Number", disabled: this.isFileSharing(),
            })}
          </Grid> */}
          <Grid item xs={12} sm={6} md={3}>
          {this.renderLabeledInput("Number of Comments Allowed", "commentsAllowed", false, {
            type: "text",
            placeholder: "Enter Number",
            disabled: this.isFileSharing(),
            inputProps: { autoComplete: "off", inputMode: "numeric", pattern: "[0-9]*" },
            onKeyDown: (e: React.KeyboardEvent) => {
              if (!/[0-9]/.test(e.key) && !["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) e.preventDefault();
            },
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value.replace(/\D/g, "");
              this.setState({ commentsAllowed: val } as any);
            },
          })}
          </Grid>

          {/* ── Estimated Reset Date ── */}
          <Grid item xs={12} sm={6} md={3}>
            {this.renderLabeledInput("Estimated Reset Date", "estimatedResetDate", false, {
              type: "date", InputLabelProps: { shrink: true }, inputProps: { min: today },
            })}
          </Grid>

          {/* ── Project Expiration Date ── */}
          <Grid item xs={12} sm={6} md={3}>
            {this.renderLabeledInput("Project Expiration Date", "projectExpirationDate", true, {
              type: "date", InputLabelProps: { shrink: true }, inputProps: { min: today },
            })}
          </Grid>

          {/* ── Timezone ── */}
          {this.renderDropdown("Timezone", "timezone_id", "openTimezoneDropdown",
            [{ id: 5, name: "AKST" }, { id: 2, name: "CST" }, { id: 1, name: "EST" }, { id: 6, name: "HAST" }, { id: 3, name: "MST" }, { id: 4, name: "PST" }],
            "id", "name", true
          )}

          {/* ── Automated Announcement ── */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography className={classes.label}>Automated Announcement</Typography>
            <FormControlLabel
              control={<YellowSwitch checked={this.state.automatedAnnouncement} onChange={this.handleSwitchChange} color="primary" />}
              label=""
            />
          </Grid>

          {/* ── Automated Data Processing ── */}
          {!this.isProjectAdmin() && <Grid item xs={12} sm={6} md={3}>
            <Typography className={classes.label}>Enable Reporting</Typography>
            <FormControlLabel
              control={<YellowSwitch
                checked={this.state.automatedDataProcessing}
                onChange={this.handleDataProcessingSwitchChange}
                color="primary"
                disabled={this.isInternalProjectAdmin() || this.isProjectAdmin()}
              />}
              label={""}
            />
          </Grid>}

          {/* ── Survey Name ── */}
          <Grid item xs={12} sm={6} md={3}>
            {this.renderLabeledInput("Survey Name", "surveyTitle", false, { placeholder: "Enter Survey Name" })}
          </Grid>

          {/* ── Survey Link ── xs=12, sm=12, md=6 */}
          <Grid item xs={12} sm={12} md={6}>
            {this.renderLabeledInput("Survey Link", "surveyLink", false, { placeholder: "Enter Survey Link" })}
          </Grid>

          {/* ── Save row ── */}
          <Grid item xs={12}>
            <div className={classes.saveRow}>
              <div>
                {this.state.saveMessage && (
                  <Typography style={{
                    fontSize: 12, padding: "6px 10px", borderRadius: "6px",
                    border: "1px solid #ddd",
                    background: this.state.saveMessage.includes("saved") ? "rgba(0,128,0,0.08)" : "rgba(255,0,0,0.08)",
                    color: this.state.saveMessage.includes("saved") ? "green" : "red",
                    display: "inline-block",
                  }}>
                    {this.state.saveMessage}
                  </Typography>
                )}
              </div>
              <Button
                variant="contained"
                color="primary"
                onClick={this.handleSubmitAndReturn}
                disabled={this.state.saving || this.state.isProjectLoading}
                style={{
                  fontWeight: 500, fontSize: "12px", borderRadius: "8px",
                  outline: "none", border: "none", background: "#E5B42F",
                  color: "#000", cursor: "pointer", transition: "all 0.2s",
                  opacity: this.state.saving || this.state.isProjectLoading ? 0.7 : 1,
                }}
              >
                {this.state.isProjectLoading ? "Loading..." : this.state.saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </Grid>

          {/* ── File Upload ── */}
          <Grid item xs={12}>
            <CommonFileUpload
              prefix="ATTACHMENT"
              url={`${config.API_SERVICE_URL}/api/upload-attachments`}
              label="Project Files:"
              multiple={true}
              chain={this.state.chain}
              dataQa="file-sharing-project-file-upload"
              projectId={this.props?.project?.id}
              fileStatus="Original"
              accept={acceptedProjectFileTypes}
              user={this.props.user}
              enableCsvDownload={false}
              disabled={!enableUpload || this.state.isProjectLoading}
              project={this.props.project}
              beforeUploadFunction={this.handleSubmitAndReturn}
            />
          </Grid>

          {/* ── File Table ── */}
          {projectFiles && projectFiles.length > 0 && (
            <Grid item xs={12}>
              <div style={{ marginTop: 20 }}>
                <div className={classes.fileHeaderRow}>
                  <Typography style={{ fontSize: 12, fontWeight: 600 }}>
                    Uploaded Project Files:
                  </Typography>
                  {this.state.selectedFiles.length > 0 && (
                    <Button
                      variant="contained"
                      onClick={this.handleBulkAction}
                      disabled={this.state.isDeletingBulk}
                      style={{
                        fontWeight: 500, fontSize: "12px", borderRadius: "8px",
                        outline: "none", border: "none", background: "#E5B611",
                        color: "#000", cursor: this.state.isDeletingBulk ? "not-allowed" : "pointer",
                        transition: "all 0.2s", padding: "6px 16px",
                        opacity: this.state.isDeletingBulk ? 0.6 : 1,
                      }}
                    >
                      {this.state.isDeletingBulk ? "Deleting..." : `Delete Selected (${this.state.selectedFiles.length})`}
                    </Button>
                  )}
                </div>
                {/* Horizontal scroll wrapper for narrow screens */}
                <div className={classes.tableWrapper}>
                  <LightThemeTable data={projectFiles} columns={fileColumns} currentPage={this.state.tableCurrentPage} onPageChange={(page: number) => this.setState({ tableCurrentPage: page })} />
                </div>
              </div>
            </Grid>
          )}

          {/* ── Delete Modal ── */}
          {this.state.showDeleteModal && (
            <div
              style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: "rgba(0,0,0,0.6)",
                display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1400,
              }}
              onClick={() => this.setState({ showDeleteModal: false, fileToDelete: 0, mapTypeToDelete: 0 })}
            >
              <div
                style={{
                  backgroundColor: "white", borderRadius: "12px", padding: "24px",
                  maxWidth: "450px", width: "90%", boxSizing: "border-box",
                  boxShadow: "0 10px 40px rgba(0,0,0,0.2)", textAlign: "left", color: "black",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 style={{ marginBottom: "16px", fontSize: "12px", color: "black" }}>Are you sure?</h3>
                <p style={{ marginBottom: "24px", fontSize: "12px", color: "black" }}>
                  Do you really want to delete this file mapping?
                </p>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "20px", flexWrap: "wrap" }}>
                  <button
                    onClick={() => this.setState({ showDeleteModal: false, fileToDelete: 0, mapTypeToDelete: 0 })}
                    style={{ padding: "8px 16px", backgroundColor: "#E5B611", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px", color: "black" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      await this.handleDeleteFileConfirmed();
                      this.setState({ showDeleteModal: false, fileToDelete: 0, mapTypeToDelete: 0 });
                    }}
                    style={{ padding: "8px 16px", backgroundColor: "#E5B611", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px", color: "black" }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

        </Grid>
      </Card>
    );
  }
}

export default connecter(withRouter(withStyles(styles)(CreateProjectStepOne)));